import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Scroll-pinned sekcja: 1. item ma sub-stan (eyebrow → related + title scale),
// dalsze itemy wjeżdżają z dołu. Pin startuje gdy section.top dotknie spodu
// headera (uwzględniając margin-bottom). Podczas pinu wymuszamy widoczność
// SmartHeadera, żeby się nie chował (data-force-visible).
export function StickySection() {
    document.addEventListener('DOMContentLoaded', () => {
        const sections = document.querySelectorAll('[data-sticky-section]');
        sections.forEach(setupStickySection);
    });
}

function setupStickySection(section) {
    const items = Array.from(section.querySelectorAll('.sticky-section__item'));
    if (items.length === 0) return;

    const firstItem = items[0];
    const heading = firstItem.querySelector('.sticky-section__heading');
    const eyebrow = firstItem.querySelector('.heading__eyebrow');
    const title = firstItem.querySelector('.heading__title');
    const description = firstItem.querySelector('.heading__description');
    const related = firstItem.querySelector('.sticky-section__related');

    // Wygląd itemu liczony symetrycznie z yPercent:
    //   - scale na ITEM (cały transformuje, daje feel głębi)
    //   - opacity na CONTENT-children (a nie na sam item) → tło gradientu zostaje
    //     widoczne pod spodem, więc nawet jak oba contenty są przezroczyste w połowie
    //     tranzycji, viewport nie jest pusty.
    //
    // Linear formula opacity = 1 - distance/100 gwarantuje, że suma opacity dwóch
    // sąsiednich itemów (jeden idzie do 0, drugi przychodzi z 0) zawsze daje 1.
    // Symetria w obu kierunkach — item idący w dół (scroll up) fade'uje tak samo
    // jak idący w górę.
    //
    // Wszystkie dzieci (włącznie z related) fade'ują razem. Auto-play related leci
    // w fazie B (timeline 0.5), a slide'y w 1.0+ — nie zachodzą na siebie czasowo,
    // więc auto-play steruje related w fazie B, a slide'owy onUpdate w slideach.
    const SCALE_MIN = 0.7;

    // Opacity dochodzi do 0 przy |yp|=70. W midpoint slidu (dist 50) opacity ≈ 0.29,
    // sum ~0.57 — fade jest widoczny ale midpoint nie jest "czarny". Działa z łagodnym
    // ease (power2.inOut), więc slide nie wygląda jakby items "wskakiwały" do centrum.
    const FADE_DISTANCE = 70;

    const updateItemAppearance = (item) => {
        const yp = parseFloat(gsap.getProperty(item, 'yPercent')) || 0;
        const distance = Math.min(Math.abs(yp), 100);
        const scale = 1 - (distance / 100) * (1 - SCALE_MIN);
        const opacity = Math.max(0, Math.min(1, 1 - distance / FADE_DISTANCE));
        gsap.set(item, { scale });
        Array.from(item.children).forEach((child) => {
            gsap.set(child, { opacity });
        });
    };

    items.forEach((item, idx) => {
        gsap.set(item, { yPercent: idx * 100 });
        updateItemAppearance(item);
    });

    // Math centrowania kompozycji:
    // - Initial: heading w grid row 2 = wycentrowany w itemie. Related niewidoczny
    //   (visibility:hidden), ale ZAJMUJE row 3 layout, więc heading dalej jest w centrum.
    // - Po phase B: chcemy żeby kompozycja heading+gap+related była wycentrowana w itemie.
    //   Composition height = heading + 64 (margin) + related. Composition jest naturalnie
    //   PRZESUNIĘTA w dół o (64 + related.height) / 2 względem item-center
    //   (bo heading jest w centrum, a related zajmuje miejsce poniżej).
    //   Żeby ją wycentrować, oba elementy podjeżdżają w górę o ten dystans.
    const RELATED_GAP = 64;
    const relatedHeight = related?.offsetHeight || 0;
    const compositionShift = (RELATED_GAP + relatedHeight) / 2;

    // Description fade'uje się do opacity 0, ale ZOSTAJE w layoucie (żeby nie skakało).
    // Jego pusta wysokość siedzi między tytułem a related, więc related podjeżdża
    // dodatkowo w górę o descriptionHeight i zamyka tę lukę. Czytane raz na setupie
    // (jak relatedHeight) — póki opis jest jeszcze widoczny (pełna wysokość).
    const descriptionHeight = description?.offsetHeight || 0;
    const relatedShift = compositionShift + descriptionHeight;

    const slideTransitions = items.length - 1;

    // Mobile (≤767px): po dojechaniu do itemu ze statystykami kontynuujemy pin i
    // scroll-driven przesuwamy rząd kafelków w bok (kafelki 2+ wjeżdżają z prawej).
    // Dystans = overflow rzędu (.stats-tiles) liczony z DOM. Kroki w jednostkach
    // viewportu (overflow / innerWidth) → 1 viewport scrolla = 1 krok, spójnie z
    // resztą timeline'u. Czytane raz na setupie, jak relatedHeight — rotacja
    // urządzenia nie przelicza (zgodnie z istniejącym wzorcem).
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const statsRow = items[items.length - 1].querySelector('.stats-tiles');
    const horizontalDistance = isMobile && statsRow
        ? Math.max(0, statsRow.scrollWidth - statsRow.clientWidth)
        : 0;
    const horizontalSteps = horizontalDistance > 0
        ? horizontalDistance / window.innerWidth
        : 0;

    // 1 step (sub-stan w 1. itemie) + slide transitions + ewentualny scroll poziomy
    const totalSteps = slideTransitions + 1 + horizontalSteps;
    // Wyższy mnożnik = więcej scrolla na ten sam ruch = wolniejsza animacja.
    // 100% = 1 viewport scrolla = 1 krok timeline'a (1:1 z naturalnym scrollem),
    // żeby handoff do następnej sekcji nie był nagle dużo szybszy niż pin.
    const SCROLL_LENGTH_PER_STEP = 100;

    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: section,
            pin: true,
            // Funkcja zamiast string — przeliczy się przy resize (responsive)
            start: () => `top top+=${getHeaderBottomOffset()}`,
            end: `+=${totalSteps * SCROLL_LENGTH_PER_STEP}%`,
            // scrub: 0.5 — lekka inercja, ale wystarczająco responsywna żeby po pinie
            // przejście w naturalny scroll nie było szarpane
            scrub: 0.5,
            invalidateOnRefresh: true,
            onToggle: (self) => toggleHeaderLock(self.isActive),
            onRefresh: (self) => { if (!self.isActive) toggleHeaderLock(false); },
        },
    });

    // Faza A (0 → 0.5): scrubbed — eyebrow + description znikają, title się skaluje
    // (transform: scale z origin bottom → bez reflow, visible bottom przyklejony do
    // bottom boxa, więc related nie wygląda na zbyt odsunięty od tytułu)
    if (eyebrow) tl.to(eyebrow, { autoAlpha: 0, duration: 0.5, ease: 'power2.inOut' }, 0);
    // Description fade'uje razem z eyebrow, ale ZOSTAJE w layoucie (display niezmieniony) —
    // jego pustą wysokość kompensujemy podjeżdżając related w górę (patrz compositionShift),
    // żeby nie było skoku jaki dawał display:none.
    if (description) tl.to(description, { autoAlpha: 0, duration: 0.5, ease: 'power2.inOut' }, 0);
    if (title) tl.to(title, { scale: 8 / 12.8, transformOrigin: 'center bottom', duration: 0.5, ease: 'power2.inOut' }, 0);

    // Po fazie A: heading + related odpalają niezależną animację (NIE scrubbed).
    // Wjeżdżają z większym translateY (mocno widoczny slide-in) i własnym tempem,
    // dokładnie wtedy gdy heading skończy się zmniejszać. Direction (forward/backward)
    // z ScrollTriggera decyduje czy gramy wjazd czy wyjście.
    const RELATED_INITIAL_Y = 100;
    if (related) {
        gsap.set(related, { y: RELATED_INITIAL_Y });

        tl.call(() => {
            const direction = tl.scrollTrigger?.direction ?? 1;
            if (heading) gsap.killTweensOf(heading);
            gsap.killTweensOf(related);

            if (direction === 1) {
                if (heading) gsap.to(heading, {
                    y: -compositionShift,
                    duration: 0.7,
                    ease: 'power2.out',
                });
                gsap.to(related, {
                    autoAlpha: 1,
                    y: -relatedShift,
                    duration: 0.7,
                    ease: 'power2.out',
                });
            } else {
                if (heading) gsap.to(heading, {
                    y: 0,
                    duration: 0.4,
                    ease: 'power2.in',
                });
                gsap.to(related, {
                    autoAlpha: 0,
                    y: RELATED_INITIAL_Y,
                    duration: 0.4,
                    ease: 'power2.in',
                });
            }
        }, [], 0.5);
    }

    // Slide'y między itemami: tweenujemy tylko yPercent, a opacity + scale liczą się
    // w onUpdate z aktualnego yPercent każdego itemu (formuły wyżej). Dzięki temu
    // efekt jest symetryczny — kierunek scrolla nie ma znaczenia, item oddalający
    // się od centrum zawsze przygasza/kurczy się.
    for (let i = 0; i < slideTransitions; i++) {
        const position = 1 + i;

        tl.to(items, {
            yPercent: '-=100',
            duration: 1,
            // power2.inOut — łagodna krzywa, items płynnie przesuwają się przez całą
            // tranzycję. expo.inOut było zbyt strome — items "wskakiwały" do centrum
            // zamiast się sunąć.
            ease: 'power2.inOut',
            onUpdate: () => items.forEach(updateItemAppearance),
        }, position);
    }

    // Scroll poziomy statystyk (tylko mobile) — startuje dokładnie gdy ostatni
    // slide wycentruje item ze statystykami (pozycja 1 + slideTransitions).
    // ease 'none' → liniowo, naturalny feel scrolla.
    if (horizontalDistance > 0) {
        tl.to(statsRow, {
            x: -horizontalDistance,
            ease: 'none',
            duration: horizontalSteps,
        }, 1 + slideTransitions);
    }

    // Paski tła (data-stripes-animation) zsynchronizowane z całym timeline'm — od
    // pozycji 0 do końca pinu. Distance interpretowany dosłownie (jak w StripesAnimation,
    // gdzie default = 20%), niezależnie od długości pinu.
    const stripesEl = section.querySelector('[data-stripes-animation]');
    const stripesImg = stripesEl?.querySelector('img');
    if (stripesImg) {
        const distance = parseFloat(stripesEl.dataset.stripesAnimation) || 20;
        const BASE_X = -50;
        gsap.set(stripesImg, { x: 0, xPercent: BASE_X });
        tl.to(stripesImg, {
            x: 0,
            xPercent: BASE_X + distance,
            ease: 'none',
            duration: totalSteps,
        }, 0);
    }
}

// Pełna wysokość headera w viewportzie = height + margin-bottom.
// Bez margin-bottom sekcja "wchodzi" pod margines headera przed zastickowaniem.
function getHeaderBottomOffset() {
    const header = document.querySelector('[data-smart-header]');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    const marginBottom = parseFloat(getComputedStyle(header).marginBottom) || 0;
    return rect.height + marginBottom;
}

// Wymuszenie widoczności SmartHeadera podczas pinu — flaga read'owana w SmartHeader.js
function toggleHeaderLock(active) {
    document.querySelectorAll('[data-smart-header]').forEach((header) => {
        if (active) {
            header.dataset.forceVisible = 'true';
            header.dataset.smartHeader = 'visible';
        } else {
            delete header.dataset.forceVisible;
        }
    });
}
